import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Tag, Modal, Form, Input, InputNumber, Switch, message, Space, Select, Popconfirm, Tooltip } from 'antd';
import { EditOutlined, KeyOutlined, PlusOutlined, DeleteOutlined, StarOutlined, StarFilled, SettingOutlined } from '@ant-design/icons';
import {
  getProviders,
  createProvider,
  updateProvider,
  deleteProvider,
  createProviderModel,
  updateProviderModel,
  deleteProviderModel,
  setProviderModelDefault,
  ProviderConfig,
  ProviderModel,
} from '../../api/providers';

const modelTypeColors: Record<string, string> = {
  llm: 'blue',
  stt: 'green',
  tts: 'purple',
  multimodal: 'orange',
};

const modelTypeLabels: Record<string, string> = {
  llm: 'LLM',
  stt: 'STT',
  tts: 'TTS',
  multimodal: 'Multimodal',
};

const Providers: React.FC = () => {
  const [providers, setProviders] = useState<ProviderConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState<{ visible: boolean; provider: ProviderConfig | null }>({
    visible: false,
    provider: null,
  });
  const [createModal, setCreateModal] = useState(false);
  const [modelModal, setModelModal] = useState<{
    visible: boolean;
    provider: ProviderConfig | null;
    model: ProviderModel | null;
  }>({ visible: false, provider: null, model: null });
  const [manageModelModal, setManageModelModal] = useState<{
    visible: boolean;
    provider: ProviderConfig | null;
  }>({ visible: false, provider: null });
  const [createForm] = Form.useForm();
  const [form] = Form.useForm();
  const [modelForm] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getProviders();
      setProviders(data);
    } catch {
      message.error('获取 Provider 列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = (provider: ProviderConfig) => {
    form.setFieldsValue({
      providerName: provider.providerName,
      apiBaseUrl: provider.apiBaseUrl,
      isEnabled: provider.isEnabled,
      isDefault: provider.isDefault,
      timeoutMs: provider.timeoutMs,
      priority: provider.priority,
      dailyLimit: provider.dailyLimit,
      extraConfig: JSON.stringify(provider.extraConfig || {}, null, 2),
      apiKey: provider.apiKeyMasked || '',
      apiSecret: provider.apiSecretMasked || '',
    });
    setEditModal({ visible: true, provider });
  };

  const handleCreate = () => {
    createForm.resetFields();
    createForm.setFieldsValue({
      isEnabled: true,
      isDefault: false,
      timeoutMs: 30000,
      priority: 0,
    });
    setCreateModal(true);
  };

  const handleCreateSubmit = async () => {
    try {
      const values = await createForm.validateFields();
      const data: any = { ...values };
      if (data.extraConfig) {
        try {
          data.extraConfig = JSON.parse(data.extraConfig);
        } catch {
          message.error('extraConfig 格式错误，请输入有效的 JSON');
          return;
        }
      }
      if (!data.apiKey) delete data.apiKey;
      if (!data.apiSecret) delete data.apiSecret;
      if (data.dailyLimit === null || data.dailyLimit === undefined) delete data.dailyLimit;
      await createProvider(data);
      message.success('创建成功');
      setCreateModal(false);
      fetchData();
    } catch {
      // error handled by interceptor
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!editModal.provider) return;
      const data: any = { ...values };
      if (data.extraConfig) {
        try {
          data.extraConfig = JSON.parse(data.extraConfig);
        } catch {
          message.error('extraConfig 格式错误，请输入有效的 JSON');
          return;
        }
      }
      if (!data.apiKey) delete data.apiKey;
      if (!data.apiSecret) delete data.apiSecret;
      if (data.dailyLimit === null || data.dailyLimit === undefined) delete data.dailyLimit;
      await updateProvider(editModal.provider.providerCode, data);
      message.success('更新成功');
      setEditModal({ visible: false, provider: null });
      fetchData();
    } catch {
      // error handled by interceptor
    }
  };

  const handleAddModel = (provider: ProviderConfig) => {
    modelForm.resetFields();
    modelForm.setFieldsValue({
      isEnabled: true,
      isDefault: false,
      priority: 0,
    });
    setModelModal({ visible: true, provider, model: null });
  };

  const handleManageModels = (provider: ProviderConfig) => {
    setManageModelModal({ visible: true, provider });
  };

  const handleEditModel = (provider: ProviderConfig, model: ProviderModel) => {
    modelForm.setFieldsValue({
      modelName: model.modelName,
      modelType: model.modelType,
      displayName: model.displayName,
      isEnabled: model.isEnabled,
      isDefault: model.isDefault,
      priority: model.priority,
      extraConfig: model.extraConfig ? JSON.stringify(model.extraConfig, null, 2) : '',
    });
    setModelModal({ visible: true, provider, model });
  };

  const handleModelSubmit = async () => {
    try {
      const values = await modelForm.validateFields();
      if (!modelModal.provider) return;
      
      const data: any = { ...values };
      if (data.extraConfig) {
        try {
          data.extraConfig = JSON.parse(data.extraConfig);
        } catch {
          message.error('extraConfig 格式错误，请输入有效的 JSON');
          return;
        }
      }
      if (!data.extraConfig) delete data.extraConfig;

      if (modelModal.model) {
        await updateProviderModel(modelModal.model.id, data);
        message.success('更新模型成功');
      } else {
        await createProviderModel(modelModal.provider.providerCode, data);
        message.success('添加模型成功');
      }
      setModelModal({ visible: false, provider: null, model: null });
      setManageModelModal({ visible: false, provider: null });
      fetchData();
    } catch {
      // error handled by interceptor
    }
  };

  const handleDeleteModel = async (model: ProviderModel) => {
    try {
      await deleteProviderModel(model.id);
      message.success('删除模型成功');
      fetchData();
    } catch {
      // error handled by interceptor
    }
  };

  const handleSetDefaultModel = async (model: ProviderModel) => {
    try {
      await setProviderModelDefault(model.id);
      message.success('设置默认模型成功');
      fetchData();
    } catch {
      // error handled by interceptor
    }
  };

  const handleDeleteProvider = async (provider: ProviderConfig) => {
    try {
      await deleteProvider(provider.providerCode);
      message.success('删除 Provider 成功');
      fetchData();
    } catch {
      // error handled by interceptor
    }
  };

  const columns = [
    { title: '名称', dataIndex: 'providerName' },
    { title: '编码', dataIndex: 'providerCode' },
    {
      title: '模型',
      render: (_: any, record: ProviderConfig) => {
        if (!record.models || record.models.length === 0) {
          return <Tag color="orange">未配置</Tag>;
        }
        const defaultModel = record.models.find(m => m.isDefault);
        return (
          <Space size={4} wrap>
            {defaultModel && <Tag color="blue">{defaultModel.modelName}</Tag>}
            {record.models.filter(m => !m.isDefault).slice(0, 2).map(m => (
              <Tag key={m.id}>{m.modelName}</Tag>
            ))}
            {record.models.length > 3 && <Tag>+{record.models.length - 3}</Tag>}
          </Space>
        );
      },
    },
    {
      title: '状态',
      render: (_: any, record: ProviderConfig) =>
        record.isEnabled ? <Tag color="green">启用</Tag> : <Tag color="red">禁用</Tag>,
    },
    {
      title: '默认',
      render: (_: any, record: ProviderConfig) => (record.isDefault ? <Tag color="blue">是</Tag> : '-'),
    },
    { title: '优先级', dataIndex: 'priority' },
    { title: '日限额', dataIndex: 'dailyLimit', render: (v: number | null) => v ?? '-' },
    {
      title: '凭证',
      render: (_: any, record: ProviderConfig) =>
        record.hasCredential ? <Tag icon={<KeyOutlined />} color="green">已配置</Tag> : <Tag color="orange">未配置</Tag>,
    },
    {
      title: '操作',
      width: 280,
      fixed: 'right' as const,
      render: (_: any, record: ProviderConfig) => (
        <Space wrap>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button type="link" size="small" icon={<PlusOutlined />} onClick={() => handleAddModel(record)}>
            添加模型
          </Button>
          <Button type="link" size="small" icon={<SettingOutlined />} onClick={() => handleManageModels(record)}>
            管理模型
          </Button>
          <Popconfirm
            title="确定删除此 Provider？"
            description="删除后将同时删除所有关联的模型和凭证"
            onConfirm={() => handleDeleteProvider(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const modelColumns = [
    { title: '模型名称', dataIndex: 'modelName' },
    {
      title: '类型',
      dataIndex: 'modelType',
      render: (v: string) => <Tag color={modelTypeColors[v]}>{modelTypeLabels[v] || v.toUpperCase()}</Tag>,
    },
    { title: '显示名称', dataIndex: 'displayName', render: (v: string) => v || '-' },
    {
      title: '状态',
      render: (_: any, record: ProviderModel) =>
        record.isEnabled ? <Tag color="green">启用</Tag> : <Tag color="red">禁用</Tag>,
    },
    {
      title: '默认',
      render: (_: any, record: ProviderModel) =>
        record.isDefault ? (
          <Tag icon={<StarFilled />} color="gold">默认</Tag>
        ) : (
          <Tooltip title="设为默认">
            <Button type="link" size="small" icon={<StarOutlined />} onClick={() => handleSetDefaultModel(record)} />
          </Tooltip>
        ),
    },
    { title: '优先级', dataIndex: 'priority' },
    {
      title: '操作',
      fixed: 'right' as const,
      render: (_: any, record: ProviderModel) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditModel(manageModelModal.provider!, record)}>
            编辑
          </Button>
          {!record.isDefault && (
            <Popconfirm title="确定删除此模型？" onConfirm={() => handleDeleteModel(record)}>
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Card
          title="Provider 配置"
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              新增
            </Button>
          }
          styles={{ body: { flex: 1, overflow: 'auto', padding: 24 } }}
          style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <Table dataSource={providers} columns={columns} rowKey="id" loading={loading} pagination={false}
            scroll={{ x: 'max-content', y: 'calc(100vh - 290px)' }} />
        </Card>
      </div>
      <Modal
        title="编辑 Provider"
        open={editModal.visible}
        onOk={handleSubmit}
        onCancel={() => setEditModal({ visible: false, provider: null })}
        width={640}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="名称" name="providerName">
            <Input />
          </Form.Item>
          <Form.Item label="API Base URL" name="apiBaseUrl">
            <Input />
          </Form.Item>
          <Space size={24}>
            <Form.Item label="启用" name="isEnabled" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item label="设为默认" name="isDefault" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
          <Space size={24}>
            <Form.Item label="超时 (ms)" name="timeoutMs">
              <InputNumber min={1000} step={1000} style={{ width: 200 }} />
            </Form.Item>
            <Form.Item label="优先级" name="priority">
              <InputNumber min={0} style={{ width: 200 }} />
            </Form.Item>
          </Space>
          <Form.Item label="日调用限额" name="dailyLimit">
            <InputNumber min={0} placeholder="不限制请留空" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="API Key" name="apiKey">
            <Input.Password placeholder="已脱敏展示，修改请填写新值" />
          </Form.Item>
          <Form.Item label="API Secret" name="apiSecret">
            <Input.Password placeholder="已脱敏展示，修改请填写新值" />
          </Form.Item>
          <Form.Item label="额外配置 (JSON)" name="extraConfig">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="新增 Provider"
        open={createModal}
        onOk={handleCreateSubmit}
        onCancel={() => setCreateModal(false)}
        width={640}
      >
        <Form form={createForm} layout="vertical">
          <Form.Item label="编码" name="providerCode" rules={[{ required: true, message: '请输入编码' }]}>
            <Input placeholder="如 openai、azure、deepseek、volcengine 等" />
          </Form.Item>
          <Form.Item label="名称" name="providerName" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="如 OpenAI、DeepSeek 等" />
          </Form.Item>
          <Form.Item
            label="API Base URL"
            name="apiBaseUrl"
            extra="留空则使用 OpenAI 官方地址"
          >
            <Input placeholder="如 https://api.openai.com/v1、https://api.deepseek.com" />
          </Form.Item>
          <Space size={24}>
            <Form.Item label="启用" name="isEnabled" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item label="设为默认" name="isDefault" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
          <Space size={24}>
            <Form.Item label="超时 (ms)" name="timeoutMs">
              <InputNumber min={1000} step={1000} style={{ width: 200 }} placeholder="默认 30000" />
            </Form.Item>
            <Form.Item label="优先级" name="priority">
              <InputNumber min={0} style={{ width: 200 }} placeholder="数字越小优先级越高" />
            </Form.Item>
          </Space>
          <Form.Item label="日调用限额" name="dailyLimit">
            <InputNumber min={0} placeholder="不限制请留空，如 1000" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="API Key" name="apiKey">
            <Input.Password placeholder="如 sk-xxxxxxxxxxxx" />
          </Form.Item>
          <Form.Item label="API Secret" name="apiSecret">
            <Input.Password placeholder="如部分厂商需要" />
          </Form.Item>
          <Form.Item label="额外配置 (JSON)" name="extraConfig">
            <Input.TextArea rows={4} placeholder='如 {"temperature": 0.7}' />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={modelModal.model ? '编辑模型' : '添加模型'}
        open={modelModal.visible}
        onOk={handleModelSubmit}
        onCancel={() => setModelModal({ visible: false, provider: null, model: null })}
        width={500}
      >
        <Form form={modelForm} layout="vertical">
          <Form.Item label="模型名称" name="modelName" rules={[{ required: true, message: '请输入模型名称' }]}>
            <Input placeholder="如 gpt-4o、whisper-1、gemini-pro-audio" />
          </Form.Item>
          <Form.Item label="模型类型" name="modelType" rules={[{ required: true, message: '请选择模型类型' }]}>
            <Select placeholder="请选择模型类型">
              <Select.Option value="llm">LLM（大语言模型）</Select.Option>
              <Select.Option value="stt">STT（语音转文字）</Select.Option>
              <Select.Option value="tts">TTS（文字转语音）</Select.Option>
              <Select.Option value="multimodal">Multimodal（多模态模型）</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="显示名称" name="displayName">
            <Input placeholder="如 GPT-4o（可选）" />
          </Form.Item>
          <Space size={24}>
            <Form.Item label="启用" name="isEnabled" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item label="设为默认" name="isDefault" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
          <Form.Item label="优先级" name="priority">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="数字越小优先级越高" />
          </Form.Item>
          <Form.Item label="额外配置 (JSON)" name="extraConfig">
            <Input.TextArea rows={3} placeholder='如 {"temperature": 0.7}' />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={`模型管理 - ${manageModelModal.provider?.providerName || ''}`}
        open={manageModelModal.visible}
        footer={null}
        onCancel={() => setManageModelModal({ visible: false, provider: null })}
        width={800}
      >
        {manageModelModal.provider && (
          <Table
            dataSource={manageModelModal.provider.models}
            columns={modelColumns}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
          />
        )}
      </Modal>
    </>
  );
};

export default Providers;
